import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Loader from 'components/Layout/Loader';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import Button from 'components/form/Button';
import TextInput from 'components/form/TextInput';
import { useDispatch, useSelector } from 'react-redux';
import { handleEditRequestEvent, showSuccess } from 'store/slices';
import { getDataFromLocalStorage, objectToFormData } from 'utils/helpers';
import { Table } from 'react-bootstrap';
import Label from 'components/form/Label';
import usePermission from 'hooks/usePermission';

const ServicesDataRequestForm = ({
  isModal,
  handleNext,
  isViewMode,
  fetchEventDetails,
}) => {
  const params = useParams();
  const { eventId } = params || {};
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [addOnServicesData, setAddOnServicesData] = useState([]);
  const pushRef = useRef(null);
  const dispatch = useDispatch();

  const userData = getDataFromLocalStorage();
  const { requestEventData } = useSelector((state) => ({
    requestEventData: state.global.requestEventData,
  }));
  const { viewAccess, createAccess, updateAccess, deleteAccess } = usePermission("Requests");

  const { user_type } = userData;
  const handleSave = async (values, { setSubmitting }) => {
    setLoading(true);
    const payload = {
      service_data: JSON.stringify(values?.services) || [],
      request_id: eventId || '',
      paper_submission_service: values?.visibility?.paper_submission_service
        ? 1
        : 0,
      plag_revision_service: values?.visibility?.plag_revision_service ? 1 : 0,
      reviewer_revision_service: values?.visibility?.reviewer_revision_service
        ? 1
        : 0,
      // comment: values?.comment,
      add_on_service: true,
    };

    const response = await dispatch(
      handleEditRequestEvent(objectToFormData(payload))
    );

    if (response?.status === 200) {
      dispatch(showSuccess(response?.message));
      fetchEventDetails();
    }
    setLoading(false);
    setSubmitting(false);
  };

  useEffect(() => {
    setAddOnServicesData(requestEventData?.add_on_service);
  }, [requestEventData?.add_on_service]);

  const initialValues = {
    services: addOnServicesData?.services?.length
      ? addOnServicesData?.services?.map((service) => ({
        id: service.id || '',
        category: service.category || '',
        free_member: service.free_member ?? 0,
        premium_member: service.premium_member ?? 0,
      }))
      : [
        {
          id: '',
          category: '',
          free_member: '',
          premium_member: '',
        },
      ],
    // comment: '',
    visibility: {
      paper_submission_service:
        addOnServicesData?.paper_submission_service !== '0' ? true : false,
      plag_revision_service:
        addOnServicesData?.plag_revision_service !== '0' ? true : false,
      reviewer_revision_service:
        addOnServicesData?.reviewer_revision_service !== '0' ? true : false,
    },
  };

  const validationSchema = Yup.object().shape({
    services: Yup.array().of(
      Yup.object().shape({
        category: Yup.string().required('Add-on service is required'),
        free_member: Yup.number()
          .typeError('Must be a number')
          .required('Free Member is Required'),
        premium_member: Yup.number()
          .typeError('Must be a number')
          .required('Premium Member is Required'),
      })
    ),
  });



  return (
    <>
      {false ? (
        <div className="cpt-80 cpb-80">
          <Loader size="md" />
        </div>
      ) : (
        <div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSave}
            validationSchema={validationSchema}
          >
            {({
              values,
              errors,
              handleChange,
              setFieldValue,
              dirty,
              resetForm,
            }) => (
              <Form>
                <FieldArray name="services">
                  {({ push, remove }) => {
                    pushRef.current = push; // Store push reference properly

                    return (
                      <>
                        {values.services.map((service, index) => (
                          <div key={index} className="row g-3">
                            {/* Category Field */}
                            <div className="col-lg-4 col-md-6 col-12">
                              <TextInput
                                label="Category"
                                placeholder="Category"
                                id={`services.${index}.category`}
                                value={values.services?.[index]?.category}
                                disabled={isViewMode}
                                onChange={(e) => {
                                  handleChange(e);
                                  if (user_type === '6') {
                                    if (addOnServicesData?.services?.[index]) {
                                      const isUpdated =
                                        e.target.value !==
                                        addOnServicesData.services[index]
                                          .category;
                                      setFieldValue(
                                        `services.${index}.is_updated_category`,
                                        isUpdated
                                      );
                                    } else {
                                      // If it's a new entry, set `is_updated_category` to true
                                      setFieldValue(
                                        `services.${index}.is_updated_category`,
                                        true
                                      );
                                    }
                                  }
                                }}
                                error={errors.services?.[index]?.category}
                              />
                            </div>

                            {/* Free Membership Field */}
                            <div className="col-lg-3 col-md-6 col-12">
                              <TextInput
                                label="Free Membership"
                                placeholder="Free Membership"
                                id={`services.${index}.free_member`}
                                value={values.services?.[index]?.free_member}
                                disabled={isViewMode}
                                onChange={(e) => {
                                  handleChange(e);

                                  if (user_type === '6') {
                                    if (addOnServicesData?.services?.[index]) {
                                      const isUpdated =
                                        Number(e.target.value) !==
                                        Number(
                                          addOnServicesData?.services[index]
                                            .free_member
                                        );
                                      setFieldValue(
                                        `services.${index}.is_updated_free_membership`,
                                        isUpdated
                                      );
                                    } else {
                                      // If it's a new entry, set `is_updated_category` to true
                                      setFieldValue(
                                        `services.${index}.is_updated_free_membership`,
                                        true
                                      );
                                    }
                                  }
                                }}
                                error={errors.services?.[index]?.free_member}
                              />
                            </div>

                            {/* Premium Membership Field */}
                            <div className="col-lg-3 col-md-6 col-12">
                              <TextInput
                                label="Premium Membership"
                                placeholder="Premium Membership"
                                id={`services.${index}.premium_member`}
                                value={values.services?.[index]?.premium_member}
                                disabled={isViewMode}
                                onChange={(e) => {
                                  handleChange(e);

                                  if (user_type === '6') {
                                    if (addOnServicesData?.services?.[index]) {
                                      const isUpdated =
                                        Number(e.target.value) !==
                                        Number(
                                          addOnServicesData?.services[index]
                                            .premium_member
                                        );
                                      setFieldValue(
                                        `services.${index}.is_updated_premium_membership`,
                                        isUpdated
                                      );
                                    } else {
                                      // If it's a new entry, set `is_updated_category` to true
                                      setFieldValue(
                                        `services.${index}.is_updated_premium_membership`,
                                        true
                                      );
                                    }
                                  }
                                }}
                                error={errors.services?.[index]?.premium_member}
                              />
                            </div>

                            {/* Remove Button */}
                            {!isViewMode &&
                              (updateAccess === '1' ||
                                createAccess === '1') && (
                                <div className={`col-lg-2 col-md-3 col-12 `}>
                                  <Label label="" />
                                  <div className="d-flex">
                                    {values.services.length > 0 && (
                                      <Button
                                        icon={
                                          <i className="bi bi-trash fs-5" />
                                        }
                                        btnStyle="primary-outline"
                                        className="h-38"
                                        onClick={() => remove(index)}
                                      />
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        ))}

                        {/* Add Services Button */}
                        {!isViewMode &&
                          (updateAccess === '1' || createAccess === '1') && (
                            <div className="mt-3">
                              <Button
                                text="+ Add Services"
                                btnStyle="primary-outline"
                                className="h-35 text-14-500 text-nowrap w-auto px-3"
                                onClick={() =>
                                  push({
                                    category: '',
                                    free_member: '',
                                    premium_member: '',
                                  })
                                }
                              />
                            </div>
                          )}
                      </>
                    );
                  }}
                </FieldArray>

                {/* Visibility Configuration Table */}
                <div
                  className="cmt-20"
                  style={{ maxWidth: '438px', overflowX: 'auto' }}
                >
                  <Table bordered className="w-100 ">
                    <thead>
                      <tr>
                        <th className="text-center text-14-500-18 bg-E9EE">
                          Visibility Configuration
                        </th>
                        <th className="text-center text-14-500-18 bg-E9EE">
                          Visibility
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {Object?.keys(values?.visibility).map((key, index) => (
                        <tr key={index}>
                          <td className="text-center text-14-400-18">
                            {key === 'paper_submission_service'
                              ? 'Paper Submission'
                              : key === 'plag_revision_service'
                                ? 'Plag Revision / Rejection'
                                : 'Reviewer Revision / Rejection'}
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              name={`visibility.${key}`}
                              checked={values.visibility[key]}
                              onChange={(e) =>
                                setFieldValue(
                                  `visibility.${key}`,
                                  e.target.checked
                                )
                              }
                              disabled={isViewMode}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Buttons Section */}
                {!isViewMode && (
                  <div className="mt-3 d-flex gap-3 cmt-20 align-items-center cmb-20 justify-content-center">
                    {(updateAccess === '1' || createAccess === '1') && (
                      <Button
                        btnStyle="primary-dark"
                        text="Save"
                        className="h-37 text-14-500 text-nowrap  cps-40 cpe-40"
                        type="submit"
                        btnLoading={loading}
                        disabled={loading || !dirty}
                      />
                    )}

                    <Button
                      btnStyle="light-outline"
                      text="Reset"
                      className="h-37 text-14-500 text-nowrap  cps-40 cpe-40"
                      type="button"
                      onClick={() => {
                        if (dirty) {
                          resetForm();
                        }
                      }}
                      disabled={loading}
                    />
                  </div>
                )}
              </Form>
            )}
          </Formik>
        </div>
      )}
    </>
  );
};

export default ServicesDataRequestForm;
